import { useApprovalDetail, useApprovalSteps, useApprovalEvents, ApprovalRequestType } from "@/hooks/useApprovalSystem";
import { useAuth } from "@/contexts/AuthContext";
import { useApprovalActions } from "@/hooks/useApprovalSystem";
import { AUDIT_ACTION_CONFIG, STATUS_STYLES } from "./ApprovalRequestTypeConfig";
import { ApprovalActionBar } from "./ApprovalActionBar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { CheckCircle2, Circle, XCircle, ArrowRight, Clock } from "lucide-react";
import { toast } from "sonner";

interface Props {
  requestId: string;
  showActions?: boolean;
  onWithdraw?: () => void;
  showWithdraw?: boolean;
  selectedLineIds?: Set<string>;
  totalLineCount?: number;
}

const StepIcon = ({ status }: { status: string }) => {
  if (status === "approved") return <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />;
  if (status === "rejected") return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
  if (status === "forwarded") return <ArrowRight className="h-4 w-4 text-purple-500 shrink-0" />;
  if (status === "pending") return <Clock className="h-4 w-4 text-amber-500 shrink-0 animate-pulse" />;
  return <Circle className="h-4 w-4 text-muted-foreground shrink-0" />;
};

export const ApprovalHistoryPanel = ({
  requestId,
  showActions = true,
  onWithdraw,
  showWithdraw = false,
  selectedLineIds,
  totalLineCount = 0,
}: Props) => {
  const { user } = useAuth();
  const { data: detailData, isLoading: detailLoading } = useApprovalDetail(requestId);
  const { data: steps = [] } = useApprovalSteps(requestId);
  const { data: events = [] } = useApprovalEvents(requestId);
  const { withdraw } = useApprovalActions();

  if (detailLoading) return <div className="p-6 text-muted-foreground text-sm">Loading…</div>;
  if (!detailData) return <div className="p-6 text-muted-foreground text-sm">No data.</div>;

  const { request, assignees } = detailData;
  const myAssignee = assignees.find((a) => a.user_id === user?.id && a.status === "pending");

  const getName = (profile: { first_name: string | null; last_name: string | null; display_name: string | null } | null | undefined, fallbackId?: string) => {
    if (!profile) return fallbackId ? fallbackId.slice(0, 8) + "…" : "Unknown";
    const nm = profile.display_name ?? (`${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim());
    return nm || (fallbackId ? fallbackId.slice(0, 8) + "…" : "Unknown");
  };

  const chainItems = steps.length > 0
    ? steps.map((s) => ({
        id: s.id,
        name: getName(s.approver_profile, s.approver_user_id),
        status: s.status,
        note: s.decision_note,
        order: s.step_order,
      }))
    : assignees.map((a, i) => ({
        id: a.id,
        name: getName((a as any).user_profile, a.user_id),
        status: a.status,
        note: a.decision_note,
        order: i + 1,
      }));

  const timelineItems = events.length > 0
    ? events.map((e) => ({
        id: e.id,
        label: AUDIT_ACTION_CONFIG[e.event_type]?.label ?? e.event_type,
        dotClass: AUDIT_ACTION_CONFIG[e.event_type]?.dotClass ?? "bg-muted-foreground",
        note: e.comment,
        actor: getName(e.actor_profile),
        createdAt: e.created_at,
      }))
    : detailData.audit.map((a) => ({
        id: a.id,
        label: AUDIT_ACTION_CONFIG[a.action]?.label ?? a.action,
        dotClass: AUDIT_ACTION_CONFIG[a.action]?.dotClass ?? "bg-muted-foreground",
        note: a.note,
        actor: getName((a as any).actor_profile),
        createdAt: a.created_at,
      }));

  const handleWithdraw = async () => {
    try {
      await withdraw.mutateAsync({ requestId });
      toast.success("Request withdrawn");
      onWithdraw?.();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-auto">
      {/* ── Action Bar ── */}
      {showActions && myAssignee && (
        <div className="border-b">
          <ApprovalActionBar
            assigneeId={myAssignee.id}
            requestId={requestId}
            requestType={request.request_type as ApprovalRequestType}
            selectedLineIds={selectedLineIds}
            totalLineCount={totalLineCount}
          />
        </div>
      )}

      {/* ── Approval Chain ── */}
      <div className="p-4 border-b space-y-3">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">Approval Chain</h3>
        {chainItems.length === 0 ? (
          <p className="text-xs text-muted-foreground">No approvers assigned.</p>
        ) : (
          <div className="space-y-2">
            {chainItems.map((item) => (
              <div key={item.id} className="flex items-start gap-2">
                <div className="flex items-center gap-1.5 shrink-0 pt-0.5">
                  <span className="text-[10px] text-muted-foreground w-4 text-right">{item.order}.</span>
                  <StepIcon status={item.status} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{item.name}</span>
                    <Badge
                      variant="outline"
                      className={cn("text-[10px] shrink-0 capitalize", STATUS_STYLES[item.status] ?? "")}
                    >
                      {item.status}
                    </Badge>
                  </div>
                  {item.note && (
                    <p className="text-xs text-muted-foreground italic mt-0.5">"{item.note}"</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Timeline ── */}
      <div className="p-4 flex-1 space-y-2">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">Timeline</h3>
        {timelineItems.length === 0 ? (
          <p className="text-xs text-muted-foreground">No activity yet.</p>
        ) : (
          <div className="space-y-3">
            {timelineItems.map((item) => (
              <div key={item.id} className="flex items-start gap-2">
                <span className={cn("mt-1.5 h-2 w-2 rounded-full shrink-0", item.dotClass)} />
                <div className="min-w-0">
                  <p className="text-sm font-medium">{item.label}</p>
                  {item.note && (
                    <p className="text-xs text-muted-foreground">{item.note}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground">
                    {item.actor && <span>{item.actor} · </span>}
                    {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Withdraw ── */}
      {showWithdraw && request.status === "pending" && (
        <div className="border-t p-4">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleWithdraw}
            disabled={withdraw.isPending}
          >
            Withdraw Request
          </Button>
        </div>
      )}
    </div>
  );
};
